import { useEffect, useRef } from 'react';
import Store from '~/store/undux';
import useSetVideo from '~/hooks/useSetVideo';
import useYouTubeIframeApi from '~/hooks/useYouTubeIframeApi';

/**
 * Responsive YouTube iframe player with playlist auto-advance.
 * @param {object} [props.currentVideo] - Video object with .id; falls back to store's youtubeSelectedVideo.
 */
const YouTubePlayer = ({ currentVideo }) => {
  const store = Store.useStore();
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const { updateStore } = useSetVideo();
  const setPlaylistInfo = store.set('playlistInfo');
  const playlistInfoRef = useRef(store.get('playlistInfo'));
  const updateStoreRef = useRef(updateStore);

  useEffect(() => {
    playlistInfoRef.current = store.get('playlistInfo');
    updateStoreRef.current = updateStore;
  }, [store, updateStore]);

  const selectedVideo = store.get('youtubeSelectedVideo') ?? currentVideo;
  const resolvedId = selectedVideo?.id;

  useYouTubeIframeApi();

  useEffect(() => {
    if (typeof window === 'undefined' || !resolvedId) return;

    function initPlayer() {
      if (!containerRef.current) return;
      if (playerRef.current) {
        try {
          playerRef.current.loadVideoById(resolvedId);
        } catch {
          // loadVideoById can throw if player not ready
        }
        return;
      }

      if (!(window.YT?.Player)) return;

      playerRef.current = new window.YT.Player(containerRef.current, {
        height: '100%',
        width: '100%',
        videoId: resolvedId,
        playerVars: {
          autoplay: 1,
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          origin: process.env.NEXT_PUBLIC_BASE_URL || undefined
        },
        events: {
          onStateChange: (e) => {
            if (e.data !== window.YT.PlayerState.ENDED) return;
            const info = playlistInfoRef.current;
            const { playlistIndex, activePlaylist } = info;

            if (playlistIndex === activePlaylist?.length - 1) {
              updateStoreRef.current(0, 0);
            } else {
              setPlaylistInfo({
                ...info,
                playlistIndex: info.playlistIndex + 1
              });
            }
          }
        }
      });
    }

    let timeoutId;
    const waitForYT = () => {
      if (window.YT?.Player) {
        initPlayer();
      } else {
        timeoutId = setTimeout(waitForYT, 100);
      }
    };
    waitForYT();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [resolvedId, setPlaylistInfo]);

  if (!resolvedId) return null;

  return (
    <div className="youtube-video-wrapper">
      <div className="youtube-player">
        <div ref={containerRef} />
      </div>
    </div>
  );
};

export default YouTubePlayer;
